import {addBreadcrumb, Breadcrumb, Extras, Primitive} from '@sentry/core';
import {captureException, captureMessage, SeverityLevel} from '@sentry/react';

import {isLocalhost} from '@/utils/environment';

export const FeatureNameTags = {
  SPLITS: 'SPLITS',
  GROUPS: 'GROUPS',
} as const;

type SentryTags = {
  [key: string]: Primitive;
};

export type SentrySeverityLevel = SeverityLevel;

interface AdditionalErrorProps {
  fileName: string;
  functionName: string;
  customTitle?: string;
  data?: any;
  severity?: SentrySeverityLevel;
  extraTags?: SentryTags;
}

function buildSentryErrorName(
  error: any,
  additionalProps: AdditionalErrorProps,
): string {
  const defaultErrorName = 'UnknownAppError';
  const hasCustomTitle = Boolean(additionalProps?.customTitle);
  const hasErrorName = Boolean(error?.name);
  const hasErrorStatus = Boolean(error?.status);
  const isErrorString = typeof error === 'string';

  let name = hasErrorName ? error.name : isErrorString ? error : '';

  if (hasErrorStatus) {
    const httpError = `HTTP Error ${error.status}`;
    name = name ? `${name} - ${httpError}` : httpError;
  }

  if (hasCustomTitle) {
    name = name
      ? `${additionalProps.customTitle} - ${name}`
      : additionalProps.customTitle;
  }

  if (!name) {
    name = defaultErrorName;
  }

  return name;
}

function buildSentryErrorMessage(error: any): string {
  if (error == null) return 'UnknownAppMessageError';
  if (typeof error === 'string') return error;
  if (error?.message) return error.message;
  if (error?.error != null && typeof error.error === 'string')
    return error.error;
  return 'UnknownAppMessageError';
}

export const logBreadcrumb = (breadcrumb: Breadcrumb) => {
  if (isLocalhost) {
    console.log('Breadcrumb:', breadcrumb);
    return;
  }

  addBreadcrumb({
    message: breadcrumb.message,
    category: breadcrumb.category,
    data: breadcrumb.data,
    level: breadcrumb.level || 'info',
    type: breadcrumb.type,
  });
};

const logError = (error: any, additionalProps: AdditionalErrorProps) => {
  if (isLocalhost) {
    console.error('logError', error, additionalProps);
    return;
  }

  const sentryError = new Error();

  sentryError.name = buildSentryErrorName(error, additionalProps);
  sentryError.message = buildSentryErrorMessage(error);
  sentryError.stack = error?.stack || '';

  const errors = Array.isArray(error?.data?.errors) ? error.data.errors : [];

  const tags: SentryTags = {
    ...additionalProps?.extraTags,
    fileName: additionalProps.fileName,
    functionName: additionalProps.functionName,
    ...(error?.status !== undefined &&
      error?.status !== null && {httpCode: error.status}),
    ...(error?.code !== undefined &&
      error?.code !== null && {errorCode: error.code}),
    ...(errors?.length > 0 && {
      'errors.first.code': errors[0]?.code,
      'errors.last.code': errors[errors.length - 1]?.code,
      'errors.first.message': errors[0]?.message,
      'errors.last.message': errors[errors.length - 1]?.message,
    }),
  };

  const extra: Extras = {
    fileName: additionalProps.fileName,
    functionName: additionalProps.functionName,
    ...(error !== undefined && error !== null && {originalError: error}),
    ...(additionalProps?.data && {data: additionalProps.data}),
  };

  if (additionalProps.severity) {
    captureMessage(sentryError.name, {
      level: additionalProps.severity,
      tags,
      extra: {
        ...extra,
        customError: sentryError,
      },
    });
    return;
  }

  captureException(sentryError, {
    tags,
    extra,
  });
};

export default logError;
